// Sentry is loaded lazily, after first paint, via a dynamic import.
// This keeps @sentry/react out of the main client bundle (~70-90 kB gzip win).
// Errors that fire before Sentry loads are buffered and flushed on init.

// Public-by-design DSN per Sentry SPA documentation.
declare const __SENTRY_RELEASE__: string | undefined;

const SENTRY_DSN =
  "https://230cf2b98e4b96f6075044dbbcb0a0d9@o4511417098895360.ingest.de.sentry.io/4511417101058128";

type Captured =
  | { kind: "error"; error: unknown; ctx?: Record<string, unknown> }
  | { kind: "message"; message: string; ctx?: Record<string, unknown> };

let initialized = false;
let loaded = false;
let sentryRef: typeof import("@sentry/react") | null = null;
const buffer: Captured[] = [];

const SENSITIVE_PARAMS = ["token_hash", "code", "access_token", "refresh_token"];

function scrubUrl(input: string): string {
  try {
    const u = new URL(input, "http://_");
    let changed = false;
    for (const k of SENSITIVE_PARAMS) {
      if (u.searchParams.has(k)) {
        u.searchParams.set(k, "[redacted]");
        changed = true;
      }
    }
    return changed ? u.toString() : input;
  } catch {
    return input;
  }
}

function registerAuthUserContext(Sentry: typeof import("@sentry/react")) {
  if (typeof window === "undefined") return;
  // Lazy import the supabase client only after Sentry is ready; never block init.
  import("@/integrations/supabase/client")
    .then(({ supabase }) => {
      supabase.auth.onAuthStateChange((event, session) => {
        if (event === "SIGNED_IN" && session?.user) {
          Sentry.setUser({ id: session.user.id });
        } else if (event === "SIGNED_OUT") {
          Sentry.setUser(null);
        }
      });
    })
    .catch(() => {
      /* never break Sentry init on supabase load failure */
    });
}

function shouldDrop(msg: string) {
  const m = msg.toLowerCase();
  return (
    m.includes("resizeobserver loop") ||
    m.includes("non-error promise rejection captured") ||
    m.includes("network request failed") ||
    m.includes("load failed") ||
    m.includes("the operation was aborted") ||
    m.includes("aborterror")
  );
}

async function loadAndInit() {
  if (loaded) return;
  loaded = true;
  try {
    const Sentry = await import("@sentry/react");
    sentryRef = Sentry;
    Sentry.init({
      dsn: SENTRY_DSN,
      release: typeof __SENTRY_RELEASE__ !== "undefined" ? __SENTRY_RELEASE__ : undefined,
      environment: import.meta.env.MODE === "production" ? "production" : "development",
      enabled: import.meta.env.PROD,
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration({
          maskAllText: false,
          blockAllMedia: false,
          maskAllInputs: true,
        }),
      ],
      tracesSampleRate: 0.1,
      replaysSessionSampleRate: 0.05,
      replaysOnErrorSampleRate: 1.0,
      beforeSend(event, hint) {
        const error = hint?.originalException as Error | undefined;
        if (shouldDrop(error?.message ?? "")) return null;

        const reqUrl = event.request?.url ?? "";
        if (reqUrl.includes("/auth/callback")) return null;

        if (event.request?.url) {
          event.request.url = scrubUrl(event.request.url);
        }
        if (event.breadcrumbs) {
          for (const b of event.breadcrumbs) {
            const data = b.data as
              | { url?: unknown; from?: unknown; to?: unknown }
              | undefined;
            if (data) {
              if (typeof data.url === "string") data.url = scrubUrl(data.url);
              if (typeof data.from === "string") data.from = scrubUrl(data.from);
              if (typeof data.to === "string") data.to = scrubUrl(data.to);
            }
          }
        }
        return event;
      },
    });
    // Flush anything captured before Sentry was ready.
    for (const item of buffer.splice(0)) {
      if (item.kind === "error") Sentry.captureException(item.error, { extra: item.ctx });
      else Sentry.captureMessage(item.message, { extra: item.ctx });
    }
    registerAuthUserContext(Sentry);

  } catch {
    // Sentry failed to load — swallow, app continues to work.
  }
}

export function initSentry() {
  if (initialized || typeof window === "undefined") return;
  initialized = true;
  const schedule = (cb: () => void) => {
    const ric = (window as typeof window & { requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => void })
      .requestIdleCallback;
    if (typeof ric === "function") ric(cb, { timeout: 3000 });
    else window.setTimeout(cb, 2000);
  };
  schedule(() => {
    void loadAndInit();
  });
}

/** Forward an error to Sentry, or buffer it if Sentry hasn't loaded yet. */
export function captureError(error: unknown, ctx?: Record<string, unknown>) {
  const msg = error instanceof Error ? error.message : String(error ?? "");
  if (shouldDrop(msg)) return;
  if (sentryRef) {
    sentryRef.captureException(error, { extra: ctx });
  } else {
    if (buffer.length < 50) buffer.push({ kind: "error", error, ctx });
  }
}
